import React from 'react';

import { getContrastColorTo, hexAlpha, isDefined, isEmpty, objectEntries, objectValues } from '@nfctron/nexus-core';
import {
	Avatar,
	Collapse,
	DynamicIcon,
	type ExtendedComponent,
	Markdown,
	UiBadge,
	UiButton,
	UiDrawer,
	UiDrawerClose,
	UiDrawerContent,
	UiDrawerDescription,
	UiDrawerFooter,
	UiDrawerHeader,
	UiDrawerTitle,
	UiDrawerTrigger,
	UiFeatureIcon,
	UiTooltip,
	UiTooltipContent,
	UiTooltipTrigger,
	cn,
	isHexAlpha,
	isThemeColor,
	useContextRequired,
	useFormat,
} from '@nfctron/nexus-react';
import {
	BagIcon,
	ClickToPayLogo,
	McClickToPayBlackLogo,
	NfcChipIcon,
	NfctronTextLogo,
	QuestionCircleIcon,
	TicketStarIcon,
} from '@nfctron/nexus-react-svg';

import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, InfoIcon, StarHalfIcon, UserIcon } from 'lucide-react';

import { CounterControl } from '@root/lib/components/CounterControl/CounterControl';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { ITEM_TYPE } from '@root/lib/constants/itemType';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { useTracking } from '@root/lib/modules/tracking/TrackingProvider';
import { getThemeColor } from '@root/lib/utils/tw.config';

import { getEventTypeSensitiveComponentDisplayName } from '../../../../_resources/helpers/common';
import { type CartStoreTicketTypesSlice, useCartStore } from '../../stores/cartStore';
import { EventCartPageContext } from '../CartContext';

const bottomBadgesMaxShown = 3;
const topBadgesMaxShown = 5;

/**
 * Event/Cart/TicketType card component
 * Displays event ticket information with pricing, description, and availability
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const TicketTypeCard: React.FunctionComponent<
	ExtendedComponent<
		{
			ticketType: CartStoreTicketTypesSlice.TicketTypeConfig;
			controller?: React.ReactNode;
			openNestedId?: string | null;
			isBadgeOpen?: string | null;
			setOpenNestedId?: React.Dispatch<React.SetStateAction<string | null>>;
			setIsBadgeOpen?: React.Dispatch<React.SetStateAction<string | null>>;
		},
		'div'
	>
> = ({ ticketType, controller, className, openNestedId, setOpenNestedId, isBadgeOpen, setIsBadgeOpen, ...props }) => {
	const { trackingModule } = useTracking();
	const { t: commonT } = useTranslate(APP_I18N_NAMESPACE.COMMON);
	const format = useFormat();
	const { event } = useContextRequired(EventCartPageContext);
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, TicketTypeCard.displayName);
	const { t: typeSensitiveT } = useTranslate(
		APP_I18N_NAMESPACE.MAIN,
		getEventTypeSensitiveComponentDisplayName(TicketTypeCard.displayName, event.eventType.eventTypeId),
	);
	const removeCartedTicketOfType = useCartStore((s) => s.removeCartedTicketOfType);
	const addCartedTicketOfType = useCartStore((s) => s.addCartedTicketOfType);
	/** get maximus products for ticket type */
	// const maximusProducts = useCartStore((s) => {
	// 	const products = s.getMaximusProductsForTicketType({ ticketTypeId: ticketType.ticketTypeId });
	// 	const giftWrapProducts = products.filter((p) => p.productTypeId === CartStoreMaximusSlice.MAXIMUS_PRODUCT_TYPE.TICKET_GIFT_WRAP);
	// 	const ticketProcessingFeeProducts = products.filter((p) => p.productTypeId === CartStoreMaximusSlice.MAXIMUS_PRODUCT_TYPE.TICKET_PROCESSING_FEE);
	// 	const treeDonationProducts = products.filter((p) => p.productTypeId === CartStoreMaximusSlice.MAXIMUS_PRODUCT_TYPE.TREE_DONATION);
	// 	return {
	// 		hasTrees: treeDonationProducts.length > 0,
	// 		hasGiftWraps: giftWrapProducts.length > 0,
	// 		ticketProcessingFees: ticketProcessingFeeProducts.reduce((acc, curr) => acc + curr.price * curr.minQuantity, 0),
	// 	};
	// });
	/** get carted tickets of this type */
	const cartedTickets = useCartStore((s) => s.getCartedTicketsOfType({ ticketTypeId: ticketType.ticketTypeId }));
	/** get ticket type state */
	const ticketTypeIs = useCartStore((s) => s.getTicketTypeStateById({ ticketTypeId: ticketType.ticketTypeId }));
	/** get ticket type content entry items */
	const contentEntries = ticketType.contentProductItems.reduce(
		(acc, item) => acc + (item.itemTypeId === ITEM_TYPE.EVENT_ENTRY ? item.quantity : 0),
		0,
	);

	/** bottom side badges with features and info content */
	const badgeClassNames = cn('px-2 h-5 sm:h-6 uppercase text-xs sm:text-sm font-bold truncate');
	const bottomBadges = React.useMemo(() => {
		/** array of badge elements to display ticket features */
		const badgesMap = {
			/** low availability (<= 100 qty left) */
			lowAvailability: !ticketTypeIs.soldOut && ticketType.ticketsAvailable <= 100 && (
				<UiBadge
					key="badge-low-availability"
					color="pink"
					variant="subtle"
					rounded="full"
					className={cn(badgeClassNames, 'shrink-0')}
					leftSection={<TicketStarIcon aria-hidden="true" />}
					tooltip={typeSensitiveT('lowAvailabilityBadge.tooltip')}
				>
					{t('lowAvailabilityBadge.text')}
				</UiBadge>
			),
			/** add to cart quantifier specific (add to cart by 2 etc.) */
			addToCartQty: ticketType.extraData.addToCartQty > 1 && (
				<UiBadge
					key="badge-addToCartQty"
					color="lucky-point"
					variant="subtle"
					rounded="full"
					className={cn(badgeClassNames, 'shrink-0')}
					leftSection={<InfoIcon aria-hidden="true" />}
					tooltip={typeSensitiveT('addToCartQtyBadge.tooltip', {
						count: ticketType.extraData.addToCartQty,
						formattedCount: format.number(ticketType.extraData.addToCartQty),
					})}
				>
					{t('addToCartQtyBadge.text', {
						count: ticketType.extraData.addToCartQty,
						formattedCount: format.number(ticketType.extraData.addToCartQty),
					})}
				</UiBadge>
			),
			/** contains chips */
			chips: ticketType.chips.length > 0 && (
				<UiBadge
					key="badge-chips"
					color="lucky-point"
					variant="subtle"
					rounded="full"
					className={cn(badgeClassNames, 'shrink-0')}
					leftSection={<NfcChipIcon aria-hidden="true" />}
					tooltip={typeSensitiveT('chipsBadge.tooltip', { count: ticketType.chips.length, formattedCount: format.number(ticketType.chips.length) })}
				>
					{t('chipsBadge.text', { count: ticketType.chips.length, formattedCount: format.number(ticketType.chips.length) })}
				</UiBadge>
			),
			/** contains entries */
			entries: contentEntries > 0 && (
				<UiBadge
					key="badge-entries"
					color="lucky-point"
					variant="subtle"
					rounded="full"
					className={cn(badgeClassNames, 'shrink-0')}
					leftSection={<UserIcon aria-hidden="true" />}
					tooltip={typeSensitiveT('entriesBadge.tooltip', { count: contentEntries, formattedCount: format.number(contentEntries) })}
				>
					{t('entriesBadge.text', { count: contentEntries, formattedCount: format.number(contentEntries) })}
				</UiBadge>
			),
			/** contains extra products */
			extraProducts: ticketType.extraProducts.length > 0 && (
				<UiBadge
					key="badge-extra-products"
					color="lucky-point"
					variant="subtle"
					rounded="full"
					className={badgeClassNames}
					leftSection={<BagIcon aria-hidden="true" />}
					tooltip={typeSensitiveT('extraProducts.tooltip')}
				>
					<span className="truncate">{t('extraProducts.text')}</span>
				</UiBadge>
			),
		};

		return objectValues(badgesMap).filter(Boolean) as React.JSX.Element[];
	}, [
		badgeClassNames,
		contentEntries,
		format,
		t,
		ticketType.extraData.addToCartQty,
		ticketType.chips.length,
		ticketType.extraProducts.length,
		ticketType.ticketsAvailable,
		ticketTypeIs.soldOut,
		typeSensitiveT,
	]);

	/** get original ticketType (if exists) */
	const pricingChange = React.useMemo(() => {
		if (isDefined(ticketType.extraData.promotionCampaign) && ticketType.extraData.promotionCampaign.type === 'mc-click-to-pay') {
			return {
				showOriginalPriceDiff: ticketType.extraData.showOriginalPriceDifference ?? true,
				showOriginalPrice: ticketType.extraData.showOriginalPrice ?? true,
				originalPrice: ticketType.extraData.originalPrice,
				originalPriceDiffColor: 'black',
			};
		}

		if (isDefined(ticketType.extraData.originalPrice)) {
			const ticketEquivalent = 1 + (ticketType.extraData.addToCartQty - 1);
			const priceDiff = ticketEquivalent * Math.max(0, ticketType.extraData.originalPrice - ticketType.price);
			if (priceDiff <= 0) {
				return { showOriginalPriceDiff: false, showOriginalPrice: false, originalPrice: undefined };
			}

			return {
				showOriginalPriceDiff: ticketType.extraData.showOriginalPriceDifference,
				showOriginalPrice: ticketType.extraData.showOriginalPrice,
				originalPrice: ticketType.price + priceDiff,
			};
		}

		return { showOriginalPriceDiff: false, showOriginalPrice: false, originalPrice: undefined };
	}, [
		ticketType.extraData.addToCartQty,
		ticketType.extraData.originalPrice,
		ticketType.price,
		ticketType.extraData.promotionCampaign,
		ticketType.extraData.showOriginalPrice,
		ticketType.extraData.showOriginalPriceDifference,
	]);

	/** top side badges with features and info content */
	const topBadges = React.useMemo(() => {
		const isClickToPayPromo = ticketType.extraData.promotionCampaign?.type === 'mc-click-to-pay';
		const ctpForYouCampaignTitle = isDefined(pricingChange.originalPrice)
			? t('clickToPayForYouCampaign.withPriceTitle', {
					formattedPrice: format.price(pricingChange.originalPrice - ticketType.price, event.currency.baseCurrency.currencyId),
				})
			: t('clickToPayForYouCampaign.title');

		const badgesMap = {
			/** org partner / promo code applied */
			partner: isDefined(ticketType.orgPartner) && (
				<UiBadge
					key="badge-partner"
					color="lucky-point"
					variant="filled"
					rounded="full"
					className={cn(badgeClassNames, 'shrink-0')}
					tooltip={typeSensitiveT('partnerBadge.tooltip', { orgPartnerName: ticketType.orgPartner.namePub })}
					{...(isDefined(ticketType.orgPartner.orgPartnerBackgroundColor) &&
						isDefined(ticketType.orgPartner.orgPartnerForegroundColor) && {
							style: {
								backgroundColor: hexAlpha(ticketType.orgPartner.orgPartnerBackgroundColor),
								color: hexAlpha(ticketType.orgPartner.orgPartnerForegroundColor),
							},
						})}
					leftSection={
						<Avatar
							src={ticketType.orgPartner?.logoUrl}
							size="xs"
							variant="light"
							avatarFit="scale-down"
							radius="xl"
							color="lucky-point"
							styles={
								isDefined(ticketType.orgPartner.orgPartnerBackgroundColor) && isDefined(ticketType.orgPartner.orgPartnerForegroundColor)
									? {
											placeholder: {
												backgroundColor: hexAlpha(ticketType.orgPartner.orgPartnerBackgroundColor),
												color: hexAlpha(ticketType.orgPartner.orgPartnerForegroundColor),
											},
										}
									: { placeholder: { backgroundColor: 'transparent' } }
							}
							classNames={{ placeholder: 'ring-0!', image: 'object-scale-down' }}
						>
							<StarHalfIcon
								size={14}
								className="text-white"
								style={{
									color: isDefined(ticketType.orgPartner.orgPartnerForegroundColor)
										? hexAlpha(ticketType.orgPartner.orgPartnerForegroundColor)
										: undefined,
								}}
							/>
						</Avatar>
					}
				>
					{ticketType.orgPartner.namePub}
				</UiBadge>
			),
			/** for you campaign (CTP) */
			forYouCampaign: isClickToPayPromo && (
				<UiDrawer
					onClose={() => {
						isDefined(setIsBadgeOpen) ? setIsBadgeOpen(null) : null;
					}}
				>
					<UiDrawerTrigger asChild>
						<UiBadge
							key="badge-clickToPayForYouCampaign"
							className={cn(badgeClassNames, 'cursor-pointer')}
							variant="filled"
							color="yellow"
							rounded="full"
							leftSection={<StarHalfIcon />}
							tooltip={ctpForYouCampaignTitle}
							onClick={() => {
								isDefined(setIsBadgeOpen) ? setIsBadgeOpen('foryou') : null;
							}}
						>
							{t('clickToPayForYouCampaign.label')}
						</UiBadge>
					</UiDrawerTrigger>
					<UiDrawerContent className="mx-auto max-w-screen-sm">
						<UiDrawerHeader className="flex items-start justify-between gap-3 text-left">
							<div className="flex w-full flex-col gap-2">
								<div className="mb-6 flex items-center justify-center gap-5 rounded-md border border-zinc-200 bg-zinc-50 px-4 py-16 sm:gap-0">
									<McClickToPayBlackLogo className="h-8 w-[100px] sm:mr-[-17px] sm:w-[200px]" />
									<div className="h-8 w-[1px] bg-zinc-200" />
									<NfctronTextLogo className="h-7 w-[200px]" />
								</div>
								<UiDrawerTitle className="mb-4">
									<span className="font-semibold">{ctpForYouCampaignTitle}</span>
								</UiDrawerTitle>
								<UiDrawerDescription>
									<Markdown className="text-base text-zinc-900">
										{isDefined(pricingChange.originalPrice)
											? t('clickToPayForYouCampaign.withPriceDescriptionMd', {
													formattedPrice: format.price(pricingChange.originalPrice - ticketType.price, event.currency.baseCurrency.currencyId),
													eventName: event.namePub,
													ticketTypeName: ticketType.namePub,
												})
											: t('clickToPayForYouCampaign.descriptionMd', { eventName: event.namePub, ticketTypeName: ticketType.namePub })}
									</Markdown>
								</UiDrawerDescription>
							</div>
						</UiDrawerHeader>

						<UiDrawerFooter>
							<UiDrawerClose asChild>
								<UiButton
									size="lg"
									variant="secondary"
									onClick={() => {
										isDefined(setIsBadgeOpen) ? setIsBadgeOpen(null) : null;
									}}
								>
									{t('clickToPayForYouCampaign.closeLabel')}
								</UiButton>
							</UiDrawerClose>
						</UiDrawerFooter>
					</UiDrawerContent>
				</UiDrawer>
			),
			/** click to pay method info */
			clickToPayMethod: isClickToPayPromo && (
				<UiDrawer
					onClose={() => {
						isDefined(setIsBadgeOpen) ? setIsBadgeOpen(null) : null;
					}}
				>
					<UiDrawerTrigger asChild>
						<UiBadge
							key="badge-clickToPayPromo"
							className={cn(badgeClassNames, 'cursor-pointer')}
							variant="filled"
							rounded="full"
							leftSection={<ClickToPayLogo className="text-white" />}
							style={{ backgroundColor: '#000', borderWidth: '0px' }}
							onClick={() => {
								isDefined(setIsBadgeOpen) ? setIsBadgeOpen('ctp') : null;
							}}
						>
							{t('clickToPayPromo.label')}
						</UiBadge>
					</UiDrawerTrigger>
					<UiDrawerContent className="mx-auto max-w-screen-sm">
						<UiDrawerHeader className="flex items-start justify-between gap-3 text-left">
							<div className="flex flex-col gap-2">
								<div className="mb-6 flex items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 px-4 py-16">
									<McClickToPayBlackLogo className="h-10 w-[250px]" />
								</div>
								<UiDrawerTitle className="mb-4">
									<span className="font-semibold">{t('clickToPayPromo.title')}</span>
								</UiDrawerTitle>
								<UiDrawerDescription>
									<Markdown className="text-base text-zinc-900">{t('clickToPayPromo.descriptionMd')}</Markdown>
								</UiDrawerDescription>
							</div>
						</UiDrawerHeader>

						<UiDrawerFooter>
							<UiDrawerClose asChild>
								<UiButton
									size="lg"
									variant="secondary"
									onClick={() => {
										isDefined(setIsBadgeOpen) ? setIsBadgeOpen(null) : null;
									}}
								>
									{t('clickToPayPromo.closeLabel')}
								</UiButton>
							</UiDrawerClose>
						</UiDrawerFooter>
					</UiDrawerContent>
				</UiDrawer>
			),
			/** maximus products */
			// FIXME: improve (for testing purposes)
			// maximusProductsGiftWrap: maximusProducts.hasGiftWraps && (
			// 	<UiDrawer>
			// 		<UiDrawerTrigger asChild>
			// 			<UiBadge
			// 				color="lucky-point"
			// 				variant="subtle"
			// 				rounded="full"
			// 				className={cn(badgeClassNames, 'cursor-pointer')}
			// 				leftSection={<GiftIcon className="size-3" />}
			// 				tooltip={typeSensitiveT('maximusProducts.tooltip')}
			// 			>
			// 				{/* FIXME */}
			// 				{/* {t('maximusProducts.text')} */}
			// 				Dárkové balení
			// 			</UiBadge>
			// 		</UiDrawerTrigger>
			// 		<UiDrawerContent className="mx-auto max-w-screen-sm">
			// 			<UiDrawerHeader className="flex items-start justify-between gap-3 text-left">
			// 				<div className="flex flex-col gap-2">
			// 					<UiDrawerTitle className="mb-4">
			// 						<span className="font-semibold">
			// 							{/* FIXME */}
			// 							{/* {t('maximusProducts.title')} */}
			// 							Vstupenku je možné zabalit do dárkového balení
			// 						</span>
			// 					</UiDrawerTitle>
			// 					<UiDrawerDescription>
			// 						<Markdown className="text-base text-zinc-900">
			// 							{/* FIXME */}
			// 							{/* {t('maximusProducts.descriptionMd')} */}
			// 							Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos.
			// 						</Markdown>
			// 					</UiDrawerDescription>
			// 				</div>
			// 			</UiDrawerHeader>
			// 		</UiDrawerContent>
			// 	</UiDrawer>
			// ),
			// FIXME: remove (for testing purposes)
			// maximusProductsProcessingFee: maximusProducts.ticketProcessingFees > 0 && (
			// 	<UiBadge
			// 		color="zinc"
			// 		variant="subtle"
			// 		className={cn(badgeClassNames, 'cursor-pointer')}
			// 		leftSection={<DollarSignIcon className="size-3" />}
			// 		rounded="full"
			// 	>
			// 		{/* FIXME */}
			// 		{/* {t('maximusProducts.text')} */}
			// 		Poplatek {format.price(maximusProducts.ticketProcessingFees, event.currency.baseCurrency.currencyId)}
			// 	</UiBadge>
			// ),
			// FIXME: improve (for testing purposes)
			// maximusProductsForests: maximusProducts.hasTrees && (
			// 	<UiBadge
			// 		color="zinc"
			// 		variant="subtle"
			// 		className={cn(badgeClassNames, 'cursor-pointer')}
			// 		rounded="full"
			// 		leftSection={<TreeDeciduousIcon className="size-3" />}
			// 	>
			// 		{/* FIXME */}
			// 		{/* {t('maximusProducts.text')} */}
			// 		Podpořte lesy
			// 	</UiBadge>
			// ),
			...objectEntries(ticketType.extraData.additionalFeatures ?? {}).map(([featureKey, feature]) => {
				const featureKeyString = String(featureKey);
				const badgeNode = (
					<UiBadge
						color="yellow"
						className={cn(badgeClassNames, { 'cursor-pointer': isDefined(feature.sheet) })}
						variant="filled"
						rounded="full"
						leftSection={isDefined(feature.icon) ? <DynamicIcon name={feature.icon as any} /> : undefined}
						tooltip={feature.tooltip ?? feature.label}
						onClick={() => {
							isDefined(setIsBadgeOpen) ? setIsBadgeOpen('fasttrack') : null;
						}}
					>
						{feature.label}
					</UiBadge>
				);

				if (!isDefined(feature.sheet)) return badgeNode;

				return (
					<UiDrawer
						key={`badge-${featureKeyString}`}
						onClose={() => {
							isDefined(setIsBadgeOpen) ? setIsBadgeOpen(null) : null;
						}}
					>
						<UiDrawerTrigger asChild>{badgeNode}</UiDrawerTrigger>
						<UiDrawerContent className="mx-auto max-w-screen-sm">
							<UiDrawerHeader className="flex items-start justify-between gap-3 text-left">
								<div className="flex flex-col gap-2">
									{/* icon */}
									{isDefined(feature.icon) && (
										<UiFeatureIcon size="default" color="zinc">
											<DynamicIcon name={feature.icon as any} className="size-6" />
										</UiFeatureIcon>
									)}
									{/* title */}
									<UiDrawerTitle className="mb-4">
										<span className="font-semibold">{feature.sheet.title}</span>
									</UiDrawerTitle>
									{/* description / content */}
									<UiDrawerDescription>
										<Markdown className="text-base text-zinc-900">{feature.sheet.descriptionMd}</Markdown>
									</UiDrawerDescription>
								</div>
							</UiDrawerHeader>
							<UiDrawerFooter>
								<UiDrawerClose asChild>
									<UiButton
										size="lg"
										variant="secondary"
										onClick={() => {
											isDefined(setIsBadgeOpen) ? setIsBadgeOpen(null) : null;
										}}
									>
										{commonT('closeButton.text')}
									</UiButton>
								</UiDrawerClose>
							</UiDrawerFooter>
						</UiDrawerContent>
					</UiDrawer>
				);
			}),
		};

		return objectValues(badgesMap).filter(Boolean) as React.JSX.Element[];
	}, [
		badgeClassNames,
		commonT,
		event.currency.baseCurrency.currencyId,
		event.namePub,
		format,
		pricingChange.originalPrice,
		t,
		ticketType.extraData.additionalFeatures,
		ticketType.extraData.promotionCampaign?.type,
		ticketType.namePub,
		ticketType.orgPartner,
		ticketType.price,
		typeSensitiveT,
	]);

	const controllerNode = controller ?? (
		<div className={cn('mt-auto ml-auto flex flex-col items-center gap-2', { 'opacity-50': ticketTypeIs.soldOut })}>
			<CounterControl
				value={cartedTickets.length}
				onDecrement={async () => {
					/** decrease carted tickets of this type by 1 */
					removeCartedTicketOfType({ quantity: 1, ticketTypeId: ticketType.ticketTypeId, trackingModule });
				}}
				incrementActionProps={{
					disabled: ticketTypeIs.orderLimited || ticketTypeIs.soldOut || ticketType.saleStatus === 'ended' || ticketType.saleStatus === 'not-started',
				}}
				onIncrement={() => {
					/** increase carted tickets of this type by 1 */
					addCartedTicketOfType({ quantity: 1, ticketTypeId: ticketType.ticketTypeId, input: { holderName: '' }, trackingModule });
				}}
			/>
		</div>
	);

	const priceAndLabelsNode = ticketTypeIs.soldOut ? (
		/** sold out label */
		<UiTooltip>
			<UiTooltipTrigger asChild>
				<div className="flex cursor-pointer items-center gap-1 text-zinc-900">
					<span className="text-base font-bold">{t('soldOut.text')}</span>
					<QuestionCircleIcon className="size-3" />
				</div>
			</UiTooltipTrigger>
			<UiTooltipContent>{typeSensitiveT('soldOut.tooltip')}</UiTooltipContent>
		</UiTooltip>
	) : /** order limited label */
	ticketTypeIs.orderLimited ? (
		<UiTooltip>
			<UiTooltipTrigger asChild>
				<div className="flex cursor-pointer items-center gap-1 text-zinc-900">
					<span className="text-base font-bold">{t('orderLimited.text')}</span>
					<QuestionCircleIcon className="size-3" />
				</div>
			</UiTooltipTrigger>
			<UiTooltipContent>
				{typeSensitiveT('orderLimited.tooltip', {
					count: ticketType.ticketLimitOrder,
					formattedCount: format.number(ticketType.ticketLimitOrder),
				})}
			</UiTooltipContent>
		</UiTooltip>
	) : ticketType.saleStatus === 'ended' ? (
		/** sale ended label */
		<UiTooltip>
			<UiTooltipTrigger asChild>
				<div className="flex cursor-pointer items-center gap-2 text-zinc-900">
					{/** label */}
					<div className="flex items-center gap-1">
						<span className="text-base font-bold">{typeSensitiveT('saleEnded.text')}</span>
						<QuestionCircleIcon className="size-3" />
					</div>
					{/** price */}
					<span className="text-sm text-zinc-500 line-through">
						{t('originalPrice', { formattedPrice: format.price(ticketType.price, event.currency.baseCurrency.currencyId) })}
					</span>
				</div>
			</UiTooltipTrigger>
			<UiTooltipContent>{typeSensitiveT('saleEnded.tooltip')}</UiTooltipContent>
		</UiTooltip>
	) : (
		/** ticket type price label */
		<div className="flex grow gap-2">
			<span className={`text-2xl leading-normal font-bold text-${ticketType.saleStatus === 'active' ? 'black' : 'zinc-500'} xl:text-3xl`}>
				{format.price(ticketType.price, event.currency.baseCurrency.currencyId)}
			</span>
			{/** original price */}
			{isDefined(pricingChange.originalPrice) && (
				<div className="flex grow items-center gap-2">
					{pricingChange.showOriginalPriceDiff ? (
						<React.Fragment>
							{/** original price strikethrough */}
							{pricingChange.showOriginalPrice && (
								<span className={`text-sm text-zinc-500 ${pricingChange.showOriginalPriceDiff ? 'line-through' : ''}`}>
									{format.price(pricingChange.originalPrice, event.currency.baseCurrency.currencyId)}
								</span>
							)}
							{/** price diff tag */}
							<UiBadge
								color={(pricingChange?.originalPriceDiffColor as any) ?? 'lucky-point'}
								variant="filled"
								{...(isDefined(ticketType.orgPartner?.orgPartnerBackgroundColor) &&
									isDefined(ticketType.orgPartner?.orgPartnerForegroundColor) && {
										color: undefined,
										variant: 'unstyled',
										style: {
											borderWidth: 0,
											backgroundColor: hexAlpha(ticketType.orgPartner.orgPartnerBackgroundColor),
											color: hexAlpha(ticketType.orgPartner.orgPartnerForegroundColor),
										},
									})}
							>
								{format.price(ticketType.price - pricingChange.originalPrice, event.currency.baseCurrency.currencyId)}
							</UiBadge>
						</React.Fragment>
					) : (
						<React.Fragment>
							{/** original price label */}
							{pricingChange.showOriginalPrice && (
								<span className="text-md text-zinc-500">
									{t('originalPrice', {
										formattedPrice: format.price(pricingChange.originalPrice, event.currency.baseCurrency.currencyId),
									})}
								</span>
							)}
						</React.Fragment>
					)}
				</div>
			)}
		</div>
	);

	return (
		<article
			key={ticketType.ticketTypeId}
			id={ticketType.namePub.replace(/\s+/g, '-').toLowerCase()}
			className={cn(
				'relative flex min-h-[110px] grow flex-col gap-2 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-4',
				{ 'cursor-not-allowed opacity-25': ticketType.saleStatus === 'ended' },
				className,
			)}
			{...props}
			itemScope
			itemType="https://schema.org/Offer"
			aria-labelledby={`ticket-name-${ticketType.ticketTypeId}`}
		>
			{/* header */}
			{topBadges.length > 0 && (
				<header>
					{/* top badges */}
					<div className="flex items-center gap-2 pb-1" role="list" aria-label={t('ticketFeatures.label', null)}>
						{/* show truncated list of badges */}
						{topBadges.slice(0, topBadgesMaxShown)}

						{topBadges.length > topBadgesMaxShown && (
							<UiBadge
								color="lucky-point"
								variant="subtle"
								rounded="full"
								className={badgeClassNames}
								tooltip={
									<ul className="list-disc pl-3">
										{topBadges.slice(topBadgesMaxShown).map((badge) => (
											<li key={badge.key}>{badge.props.tooltip}</li>
										))}
									</ul>
								}
							>
								{`+${format.number(topBadges.length - topBadgesMaxShown)}`}
							</UiBadge>
						)}
					</div>
				</header>
			)}

			{/* main content */}
			<UiDrawer onClose={() => (isDefined(setOpenNestedId) ? setOpenNestedId(null) : null)}>
				<UiDrawerTrigger asChild>
					<div
						role="button"
						tabIndex={0}
						onClick={() => {
							isDefined(setOpenNestedId) ? setOpenNestedId(ticketType.ticketTypeId) : null;
						}}
						className="flex cursor-pointer flex-col"
					>
						{/* ticket type name */}
						<h3
							id={`ticket-name-${ticketType.ticketTypeId}`}
							className={cn('text-lucky-point-500 text-lg font-semibold md:text-xl', {
								'line-through opacity-50': ticketTypeIs.soldOut || ticketType.saleStatus === 'ended',
							})}
							itemProp="name"
						>
							{ticketType.namePub}
						</h3>
						{/* price and labels */}
						<div itemProp="offers" itemScope itemType="https://schema.org/Offer">
							{priceAndLabelsNode}
						</div>
						{/* ticket type description */}
						<div className="mt-1">
							<div className="group">
								<div className={cn('relative text-base', { 'line-through opacity-50': ticketTypeIs.soldOut })} itemProp="description">
									{isEmpty(ticketType.description) ? (
										<span className="opacity-50">{typeSensitiveT('noDescription.text')}</span>
									) : (
										<div
											className={cn('relative', 'line-clamp-5')}
											ref={(el) => {
												if (!el) return;
												/** check if content is clamped by comparing scroll height to client height */
												el.dataset.isClamped = el.scrollHeight > el.clientHeight ? 'true' : 'false';
											}}
										>
											<Markdown
												className="contents text-base"
												componentProps={{
													ul: { className: 'my-0 pl-5 contents' },
													li: { className: 'ml-5' },
												}}
											>
												{ticketType.description}
											</Markdown>
										</div>
									)}
									{/* show more button */}
									<span
										data-show-only-when-clamped
										style={{ display: 'var(--show-read-more, none)' }}
										className="text-sm font-medium text-purple-500 group-hover:underline"
										aria-label={t('readMore.ariaLabel', null)}
									>
										<span>{t('readMore.text')}</span>
										<ChevronRightIcon className="ml-2 inline size-3" aria-hidden="true" />
									</span>
									<style jsx>{`
										div:has(div[data-is-clamped='true']) [data-show-only-when-clamped] {
											--show-read-more: block;
										}
									`}</style>
								</div>
							</div>
						</div>
					</div>
				</UiDrawerTrigger>

				{/* ticket type detail drawer */}
				<UiDrawerContent className={`mx-auto max-w-screen-sm ${isBadgeOpen ? 'hidden' : ''}`}>
					<UiDrawerHeader className="shrink-0 border-b border-zinc-200 pb-6">
						<UiDrawerTitle>{ticketType.namePub}</UiDrawerTitle>
						{/* top badges */}
						{topBadges.length > 0 && (
							<div
								className="mt-2 flex flex-wrap items-center justify-center gap-2"
								style={{ opacity: ticketTypeIs.soldOut ? 0.5 : 1 }}
								role="list"
								aria-label={t('ticketFeatures.label', null)}
							>
								{topBadges}
							</div>
						)}
					</UiDrawerHeader>
					<div
						className={cn(
							'flex max-h-full flex-col gap-3 overflow-y-auto p-4 text-left text-base text-zinc-700',
							/** padding with padded bottom footer (since its fixed) */
							'pb-[96px]',
						)}
					>
						{/* price and labels */}
						{priceAndLabelsNode}

						{/* description */}
						<div>
							<h4 className="block text-base font-semibold">{typeSensitiveT('ticketTypeDetailDrawer.descriptionTitle')}</h4>
							{isEmpty(ticketType.description) ? (
								<span className="opacity-50">{typeSensitiveT('noDescription.text')}</span>
							) : (
								<Markdown className="text-base">{ticketType.description}</Markdown>
							)}
						</div>

						{/* bottom badges */}
						<div
							className="flex flex-wrap items-center gap-2"
							style={{ opacity: ticketTypeIs.soldOut ? 0.5 : 1 }}
							role="list"
							aria-label={t('ticketDetails.label', null)}
						>
							{bottomBadges}
						</div>
					</div>
					<UiDrawerFooter className="fixed right-0 bottom-0 left-0 w-full bg-linear-to-t from-white via-white/80 to-transparent">
						<UiDrawerClose asChild>
							<UiButton onClick={() => (isDefined(setOpenNestedId) ? setOpenNestedId(null) : null)} size="lg" variant="secondary">
								{commonT('closeButton.text')}
							</UiButton>
						</UiDrawerClose>
					</UiDrawerFooter>
				</UiDrawerContent>
			</UiDrawer>

			{/* footer */}
			<footer className="flex items-end justify-between gap-2">
				{/* bottom badges */}
				<div
					className="flex flex-wrap items-center gap-1 truncate md:gap-2"
					style={{ opacity: ticketTypeIs.soldOut ? 0.5 : 1 }}
					role="list"
					aria-label={t('ticketDetails.label', null)}
				>
					{/* show truncated list of badges */}
					{bottomBadges.slice(0, bottomBadgesMaxShown)}
					{/* display badge with omitted badge labels */}
					{bottomBadges.length > bottomBadgesMaxShown && (
						<UiBadge
							color="lucky-point"
							variant="subtle"
							rounded="full"
							className={badgeClassNames}
							tooltip={
								<ul className="list-disc pl-3">
									{bottomBadges.slice(bottomBadgesMaxShown).map((badge) => (
										<li key={badge.key}>{badge.props.tooltip}</li>
									))}
								</ul>
							}
						>
							+&nbsp;{format.number(bottomBadges.length - bottomBadgesMaxShown)}
						</UiBadge>
					)}
				</div>

				{/* controller */}
				{!ticketTypeIs.soldOut && <React.Fragment>{controllerNode}</React.Fragment>}
			</footer>
		</article>
	);
};

TicketTypeCard.displayName = 'Event/Cart/TicketTypeCard';

/**
 * Event/Cart/TicketTypeGroupSection component
 * Groups and displays ticket types in collapsible sections
 * @author filipditrich <ditrich@nfctron.com>
 */
export const TicketTypeGroupSection: React.FunctionComponent<
	ExtendedComponent<
		{
			ticketTypeGroup: CartStoreTicketTypesSlice.TicketTypeGroupConfig | undefined;
			ticketTypes: CartStoreTicketTypesSlice.TicketTypeConfig[];
			isOnlyGroup: boolean;
		},
		'div'
	>
> = (props) => {
	const { ticketTypeGroup, ticketTypes, className, isOnlyGroup, ...rest } = props;
	const format = useFormat();
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, TicketTypeCard.displayName);
	const toggleTicketTypeGroup = useCartStore((state) => state.toggleTicketTypeGroup);
	const ticketTypesListedGroupedOpen = useCartStore((state) => state.ticketTypesListedGroupedOpen);

	/** list of ticket type cards */
	const ticketTypeList = ticketTypes.map((ticketType) => {
		return <TicketTypeCard key={ticketType.ticketTypeId} ticketType={ticketType} />;
	});

	/** calculate color styles for the group */
	const groupColors = React.useMemo(() => {
		if (!isDefined(ticketTypeGroup) || !isDefined(ticketTypeGroup.extraData?.color)) {
			return null;
		}

		/** get background color from theme or hex value */
		const bgColor = isThemeColor(ticketTypeGroup.extraData.color)
			? getThemeColor(ticketTypeGroup.extraData.color)
			: isHexAlpha(hexAlpha(ticketTypeGroup.extraData.color))
				? hexAlpha(ticketTypeGroup.extraData.color)
				: undefined;

		const textColor = isDefined(bgColor) ? getContrastColorTo(bgColor) : undefined;
		return { backgroundColor: bgColor, color: textColor };
	}, [ticketTypeGroup]);

	/** group style with CSS variables for Tailwind v4 compatibility */
	const groupStyle = React.useMemo(() => {
		if (!isDefined(groupColors?.backgroundColor) || !isDefined(groupColors?.color)) {
			return undefined;
		}
		return {
			'--group-bg-color': groupColors.backgroundColor,
			'--group-text-color': groupColors.color,
		} as React.CSSProperties;
	}, [groupColors]);

	/** handle single group case */
	if (isOnlyGroup) {
		return (
			<div className="flex flex-col gap-2" role="region" aria-label={isDefined(ticketTypeGroup) ? ticketTypeGroup.namePub : t('defaultGroupHeader')}>
				{ticketTypeList}
			</div>
		);
	}

	/** determine if group is open */
	const isGroupOpened = ticketTypesListedGroupedOpen[ticketTypeGroup?.ticketTypeGroupId ?? 'default'] === true;
	const isOpened = isGroupOpened || ticketTypeList.length === 1;
	const groupId = ticketTypeGroup?.ticketTypeGroupId ?? 'default';
	const groupName = ticketTypeGroup?.namePub ?? t('defaultGroupHeader');

	return (
		<section className={cn('flex flex-col gap-2 rounded-xl', className)} {...rest} aria-labelledby={`ticket-group-${groupId}`}>
			{/* header */}
			<UiButton
				fullWidth
				variant="subtle"
				color="lucky-point"
				id={`ticket-group-${groupId}`}
				className={cn(
					'h-unset rounded-lg p-3 text-lg font-semibold',
					ticketTypeList.length === 1 && 'cursor-default',
					/** apply custom colors via css variables if defined */
					isDefined(groupStyle) && 'bg-[var(--group-bg-color)] text-[var(--group-text-color)]',
				)}
				onClick={() => {
					toggleTicketTypeGroup({ ticketTypeGroupId: groupId, changeTo: !isOpened, autoCloseOthers: false });
				}}
				{...(isDefined(groupStyle) && { variant: 'unstyled', style: groupStyle })}
				leftSection={
					isDefined(ticketTypeGroup) &&
					/** @ts-expect-error FIXME: logoUrl not in extraData schema yet (waiting for new nexus release) */
					!isEmpty(ticketTypeGroup.extraData.logoUrl) ? (
						<img
							className="size-6! shrink-0 object-scale-down"
							/** @ts-expect-error FIXME: logoUrl not in extraData schema yet (waiting for new nexus release) */
							src={ticketTypeGroup.extraData.logoUrl}
							alt={ticketTypeGroup.namePub}
							width="24px"
							height="24px"
						/>
					) : undefined
				}
				aria-expanded={isOpened}
				aria-controls={`ticket-group-content-${groupId}`}
			>
				<div className="flex grow items-center gap-2 truncate">
					{/* group header */}
					<h2 className="truncate" id={`ticket-group-${groupId}`}>
						{groupName}
					</h2>
					{/* count badge */}
					{ticketTypeList.length > 1 && (
						<UiBadge
							className={cn(
								'text-sm',
								/** apply custom colors via css variables if defined (inverted from button) */
								isDefined(groupStyle) && 'bg-[var(--group-text-color)] text-[var(--group-bg-color)]',
							)}
							color="lucky-point"
							{...(isDefined(ticketTypeGroup) && {
								tooltip: t('groupTicketBadgeTooltip', { count: ticketTypeList.length, group: groupName }),
							})}
						>
							<span className="hidden sm:inline">
								{t('groupTicketBadgeLabel', { count: ticketTypes.length, formattedCount: format.number(ticketTypes.length) })}
							</span>
							<span className="inline sm:hidden">{format.number(ticketTypes.length)}</span>
						</UiBadge>
					)}
					{/* collapse icon */}
					{ticketTypeList.length > 1 && (
						<div className="ml-auto">{isOpened ? <ChevronUpIcon aria-hidden="true" /> : <ChevronDownIcon aria-hidden="true" />}</div>
					)}
				</div>
			</UiButton>

			{/* ticket type list */}
			<Collapse in={isOpened} id={`ticket-group-content-${groupId}`}>
				<div className="flex flex-col gap-2">{ticketTypeList}</div>
			</Collapse>
		</section>
	);
};
